import _ from 'lodash';
import firebase from 'firebase/app';
import 'firebase/auth';
import { setSiteSubscription } from 'reducers/siteInfo';
import { setSubscription } from 'reducers/subscriptions';
import { setFetchStatus } from 'reducers/fetchStatus';
import sleep from 'util/sleep';
import { showError, showSuccess } from '../actions/utils';
import { firebaseConfigs } from '../init-firebase';
import { strings } from './strings';

// for all api methods throw an exception if something fails handleError in actions will catch the error and display a message
const { Paddle } = window;
const PRODUCT = process.env.REACT_APP_PADDLE_ENVIRONMENT === 'sandbox' ? 12767 : 649320;
export const SUBSCRIPTION = process.env.REACT_APP_PADDLE_ENVIRONMENT === 'sandbox' ? 41529 : 902308;

export class Api {

  constructor() {
    this.store = null;
    this.user = null;
    this.token = null;
    this.api = process.env.REACT_APP_BASE_API;
  }

  async initialize(store) {
    this.store = store;
    firebase.initializeApp(firebaseConfigs[process.env.REACT_APP_PADDLE_ENVIRONMENT]);
    const res = await this.getResult();

    if (!res) {
      await this.verifyUser();
    }
    await this.setPrice(PRODUCT);
    await this.setSubscription();
  }

  fetchJson = async (path, {
    authorize = false, method = 'POST', headers = {}, body, mode = 'cors', ...extra
  }) => {
    const token = authorize ? this.token : undefined;

    headers = _.pickBy({
      // eslint-disable-next-line no-prototype-builtins
      'Content-Type': body ? 'application/json' : undefined || headers.hasOwnProperty('Content-Type') ? headers['Content-Type'] : undefined,
      'Authorization': authorize ? `Bearer ${token}` : undefined,
      ...headers,
    });

    if (body && !(body instanceof FormData)) {
      body = JSON.stringify(body);
    }

    const result = await fetch(path, _.pickBy({
      method,
      headers,
      body,
      mode,
      ...extra,
    }));

    if (result?.status === 401 && authorize) {
      return;
    }

    const json = await result.json().catch(_.stubObject);

    return { status: result?.status, data: json };
  };

getResult = async () => {
  await firebase.auth()
    .getRedirectResult()
    .then(async (result) => {
      if (result.credential) {
        await this.getFireBaseID(true);
      }
    }).catch((error) => {
      showError({ title: error.message });
    });
}

verifyUser = async (isSignUp) => {
  await firebase.auth().onAuthStateChanged(async (user) => {
    if (user) {
      this.store.dispatch({ type: 'SET_USER', user });
      if (window.location.pathname !== '/auth/plugin') {
        this.token = user.za;
        if (!isSignUp) {
          await this.getAllUserData();
        }
      }
      return user;
    }
  });
}

logout = async () => {
  await firebase.auth().signOut()
    .then(() => {
      this.store.dispatch({ type: 'SET_USER', user: null });
      this.store.dispatch({ type: 'SET_ORDERS', orders: null });
      this.store.dispatch({ type: 'SET_ENTITLEMENT', entitlement: null });
    })
    .catch((error) => {
      showError({ title: error.message });
    });
}

login = async (token, isOauth, subscribeToNewsletter, isSignUp, name) => {
  const body = { token, name };
  const { status } = await this.fetchJson(`${this.api}/Auth/OAuthRegister`, { method: 'POST', body });
  if (status === 200) {
    const user = await this.verifyUser(isSignUp);
    if (subscribeToNewsletter) {
      await this.subscribeToNewsletter();
    }
    return user;
  }

  if (!isOauth) {
    this.store.dispatch({ type: 'END_OPERATION' });
  }

  await this.logout();
  showError({ title: 'There was a problem when trying to sign in' });
};

getFireBaseID = async (isOauth, subscribeToNewsletter, isSignUp) => {
  const user = await firebase.auth().currentUser;
  const { za: idToken } = user;
  const { displayName } = user;

  if (idToken) {
    try {
      await this.login(idToken, isOauth, subscribeToNewsletter, isSignUp, displayName);
    } catch (error) {
      showError({ title: error.message });
      this.store.dispatch({ type: 'END_OPERATION' });
    }
  }
};

oauth = async (provider) => {
  await firebase.auth().signInWithPopup(provider);
}

saveFirebaseUser = async (email, password) => {
  const res = await firebase.auth().signInWithEmailAndPassword(email, password);
  if (res) {
    try {
      this.store.dispatch({ type: 'SET_ERROR', error: null });
      await this.getFireBaseID(false);
      return true;
    } catch (error) {
      const { language } = this.store.getState().siteInfo;
      switch (error.code) {
        case 'auth/wrong-password':
          if (window.location.pathname === '/auth/plugin') {
            this.store.dispatch({ type: 'SET_ERROR', error: strings[language].password });
          } else {
            showError({ title: strings[language].password });
          }
          break;
        case 'auth/user-not-found':
          if (window.location.pathname === '/auth/plugin') {
            this.store.dispatch({ type: 'SET_ERROR', error: strings[language].account });
          } else {
            showError({ title: strings[language].account });
          }
          break;
        default:
          if (window.location.pathname === '/auth/plugin') {
            this.store.dispatch({ type: 'SET_ERROR', error: error.message });
          } else {
            showError({ title: error.message });
          }
          break;
      }
      this.store.dispatch({ type: 'END_OPERATION' });
    }
  }
}

loginFirebase = async (email, password, rememberMe) => {
  if (rememberMe) {
    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(async () => {
        await this.saveFirebaseUser(email, password, rememberMe);
      })
      .catch((error) => {
        showError({ title: error.message });
      });
  } else {
    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION)
      .then(async () => {
        await this.saveFirebaseUser(email, password, rememberMe);
      })
      .catch((error) => {
        showError({ title: error.message });
      });
  }
}

signUp = async (signUpData) => {
  await firebase.auth().useDeviceLanguage();
  const isCreated = await firebase.auth().createUserWithEmailAndPassword(signUpData.email, signUpData.password);
  if (isCreated) {
    try {
      const user = await firebase.auth().currentUser;
      const isUpdated = await user.updateProfile({
        displayName: signUpData.name,
      });
      if (isUpdated) {
        try {
          await this.getFireBaseID(false, signUpData.newsletter, true);
        } catch (error) {
          showError({ title: error.message });
          this.store.dispatch({ type: 'END_OPERATION' });
        }
      }
    } catch (error) {
      showError({ title: error.message });
      this.store.dispatch({ type: 'END_OPERATION' });
    }
  }
}

forgotPassword = async (email) => {
  await firebase.auth().useDeviceLanguage();
  await firebase.auth().sendPasswordResetEmail(email).then(() => {
    this.store.dispatch({ type: 'END_OPERATION' });
    showSuccess({
      title: `We have sent an email to the account "${email}" so you can reset your password`,
      className: 'modal-item',
      icon: '',
    });

  }).catch((error) => {
    showError({ title: error.message });

  });
}

changeUserName = async (data, user, history) => {
  user.updateProfile({
    displayName: data.name,
  }).then(async () => {
    const newUser = await this.getUser();
    await this.store.dispatch({ type: 'SET_USER', user: newUser });
    if (!data.newPassword) {
      showSuccess({ title: 'Your data has been updated successfully' });
      history.push('/my-account');
    }
  })
    .catch((error) => {
      showError({ title: error.message });
      return false;
    });
  return true;
}

changeUserPassword = async (data, user, history) => {
  const cred = await firebase.auth.EmailAuthProvider.credential(
    data.email,
    data.password,
  );
  user.reauthenticateWithCredential(cred).then(() => {
    user.updatePassword(data.newPassword).then(() => {
      showSuccess({ title: 'Your data has been updated successfully' });
      history.push('/my-account');
    })
      .catch((error) => {
        showError({ title: error.message });
      });
  }).catch((error) => {
    showError({ title: error.message });
  });
}

editUser = async (data, history) => {
  if (!data.password && data.newPassword) {
    showError({ title: 'You need your current password to be able to change your password' });
    this.store.dispatch({ type: 'END_OPERATION' });
    return false;
  }
  const user = await this.getUser();
  if (data.name !== user.displayName) {
    await this.changeUserName(data, user, history);
  }
  if (data.password && data.newPassword) {
    await this.changeUserPassword(data, user, history);
  }
  if (data.name === user.displayName && !data.newPassword) {
    showError({ title: 'Has not added any changes to your data' });
  }
  this.store.dispatch({ type: 'END_OPERATION' });
}

setPrice = async (product) => {
  await Paddle.Product.Prices(product, (prices) => {
    this.store.dispatch({ type: 'SET_PRICE', price: prices.price });
    this.store.dispatch({ type: 'SET_COUNTRY', country: prices.country });
  });
  this.store.dispatch({ type: 'SET_PRODUCT', product });
}

setSubscription = async () => {
  await Paddle.Product.Prices(SUBSCRIPTION, (prices) => {
    this.store.dispatch(setSiteSubscription({ prices: prices.price, id: SUBSCRIPTION }));
  });
}

getOrders = async () => {
  const { data, status } = await this.fetchJson(`${this.api}/User/Order`, { method: 'GET', authorize: true });
  if (status === 200 && data.length > 0) {
    this.store.dispatch({ type: 'SET_ORDERS', orders: data.reverse() });
  }
}

getSubs = async () => {
  const res = await this.fetchJson(`${this.api}/User/Subscription`, { method: 'GET', authorize: true });
  if (res.status === 200) {
    this.store.dispatch(setSubscription(res.data));
  }
}

pauseSub = async () => {
  await this.fetchJson(`${this.api}/User/UpdateSubscriptionStatus`, { method: 'POST', authorize: true, body: { action: true } });
  await sleep(2000);
  await this.getSubs();
}

resumeSub = async () => {
  await this.fetchJson(`${this.api}/User/UpdateSubscriptionStatus`, { method: 'POST', authorize: true, body: { action: false } });
  await sleep(2000);
  await this.getSubs();
}

getEntitlement = async () => {
  const { data, status } = await this.fetchJson(`${this.api}/User/Entitlement`, { method: 'GET', authorize: true });
  if (status === 200 && data.length > 0) {
    this.store.dispatch({ type: 'SET_ENTITLEMENT', entitlement: data.reverse()[0] });
  }
}

deactivateEntitlement = async (id) => {
  const { status } = await this.fetchJson(`${this.api}/User/Entitlement/${id}/Deactivate`, { method: 'POST', authorize: true });
  if (status === 200) {
    await this.getOrders();
    await this.getEntitlement();
    const { language } = this.store.getState().siteInfo;
    showSuccess({
      title: strings[language].deactivateEntitlement,
      className: 'modal-item',
      icon: '',
    });
  }
}

getDownloadLink = async (id, platform) => {
  const { data, status } = await this.fetchJson(`${this.api}/Entitlement/Download?Platform=${platform}${id ? `&EntitlementID=${id}` : ''}`, { method: 'GET', authorize: true });
  if (status === 200) {
    return data.downloadURL;
  }
}

getAllUserData = async () => {
  this.store.dispatch(setFetchStatus('loading'));
  await this.getEntitlement();
  await this.getOrders();
  await this.getSubs();
  this.store.dispatch(setFetchStatus('complete'));
}

getUser = async () => firebase.auth().currentUser;

subscribeToNewsletter = async () => {
  const { status } = await this.fetchJson(`${this.api}/User/SubscribeToNewsletter`, { method: 'POST', authorize: true });
  if (status !== 200) {
    showError({
      title: 'An error occurred while subscribing to the newsletter.',
      text: 'You can try again in the footer section',
      className: 'modal-item',
    });
  }
}

authPlugin = async (token, refreshToken, port) => {
  const url = `http://localhost:${port || 8912}?token=${token}&refreshToken=${refreshToken}`;
  await this.fetchJson(url, { method: 'GET', auth: false, mode: 'no-cors' });
  // this is since we do not need any response from the server only that the fetch has been made
  return true;
}

validateVoucher = async ({ voucher }) => {
  const { data, status } = await this.fetchJson(`${this.api}/Entitlement/Voucher`, {
    method: 'POST',
    authorize: true,
    body: {
      voucher,
      paddleProductID: PRODUCT,
      type: 'User',
      name: 'WalkMix Creator',
      channel: 'MI',
    },
  });

  if (status !== 201) {
    throw new Error(data.message);
  }

  const goToMyAccount = () => {
    const { language } = this.store.getState().siteInfo;
    const myAccountUrl = language === 'japan' ? '/jp/私のアカウント' : '/my-account';
    window.location.replace(`${window.location.origin}${myAccountUrl}`);
  };

  this.store.dispatch({ type: 'SET_ENTITLEMENT', entitlement: data });
  showSuccess({
    title: 'Voucher redeemed!',
    text: 'You may now download the plugin’s installer from the ‘Downloads’ section under ‘My Account’.',
  })
    .then(() => {
      goToMyAccount();
    });
}

addEmailToFreeTrialList = async ({ email }) => {
  const { data, status } = await this.fetchJson(`${this.api}/Entitlement/TrialRequest?email=${encodeURIComponent(email)}`, {
    method: 'POST',
    authorize: false,
  });

  if (status === 200) {
    if (this.token) {
      await this.getAllUserData();
    }
    return data;
  }

  if (data?.error_code_message === 'user_has_entitlement') {
    showError({
      title: 'Thank you for being our loyal customer!',
      text: `Your account already has WalkMix Creator.
      Please log in to enjoy the plug-in.`,
      button: { text: 'Log in', className: 'modal-btn' },
    })
      .then(() => {
        window.location.replace(`${window.location.origin}/my-account`);
      });
    return false;
  }
  if (data?.error_code_message === 'user_trial_requested') {
    showError({
      title: 'This email has already been registered for a free trial',
      text: `Please check your spam folder if you haven’t received the invitation email yet.
      If the invitation has not been received, please contact us at support@360ra.com.`,
    });
    return false;
  }
  if (data?.error_code_message === 'trial_limit_exceeded') {
    showError({
      title: 'This offer is no longer available',
      text: `This free trial offer is no longer available.
      Feel free to check out our other offers!`,
    });
    return false;
  }

  throw new Error(data);

}

validateFreeTrialEmail = async () => {
  const { data, status } = await this.fetchJson(`${this.api}/Entitlement/TrialRedemption`, {
    method: 'POST',
    authorize: true,
    body: {
      paddleProductID: PRODUCT,
      type: 'User',
      name: 'WalkMix Creator',
      channel: '360RA',
    },
  });

  if (status === 201) {
    this.store.dispatch({ type: 'SET_ENTITLEMENT', entitlement: data });
  } else if (status !== 404) {
    throw new Error(data);
  }

}

}

export const api = new Api();
